import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Intelligent preloading hook
 * Preloads pages based on user navigation patterns and hover intent
 */

// Page import functions for preloading
const pageImports = {
  '/dashboard': () => import('../pages/Dashboard'),
  '/settings': () => import('../pages/SettingsPage'),
  '/analytics': () => import('../pages/AnalyticsPage'),
  '/billing': () => import('../pages/BillingPage'),
  '/calendar': () => import('../pages/CalendarPage'),
  '/signatures': () => import('../pages/SignaturesPage'),
  '/teams': () => import('../pages/TeamsPage'),
};

// Navigation patterns: from -> likely next pages
const navigationPatterns = {
  '/login': ['/dashboard'],
  '/register': ['/dashboard'],
  '/dashboard': ['/settings', '/analytics', '/billing'],
  '/settings': ['/dashboard', '/billing'],
  '/analytics': ['/dashboard'],
  '/billing': ['/dashboard', '/settings'],
  '/calendar': ['/dashboard'],
  '/signatures': ['/dashboard', '/settings'],
  '/teams': ['/dashboard', '/settings'],
};

// Track preloaded pages to avoid duplicate loads
const preloadedPages = new Set();

/**
 * Preload a specific page
 */
const preloadPage = async (path) => {
  if (preloadedPages.has(path)) return;
  
  const importFn = pageImports[path];
  if (importFn) {
    try {
      preloadedPages.add(path);
      await importFn();
      console.log(`[Preload] Loaded: ${path}`);
    } catch (error) {
      console.warn(`[Preload] Failed to load: ${path}`, error);
      preloadedPages.delete(path);
    }
  }
};

/**
 * Preload multiple pages
 */
const preloadPages = (paths) => {
  paths.forEach(path => {
    // Use requestIdleCallback for non-blocking preload
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => preloadPage(path), { timeout: 2000 });
    } else {
      setTimeout(() => preloadPage(path), 100);
    }
  });
};

/**
 * Hook to enable intelligent preloading
 */
export const useIntelligentPreload = () => {
  const location = useLocation();
  const lastPreloadRef = useRef(null);

  // Preload likely next pages based on current location
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Avoid duplicate preloads for the same path
    if (lastPreloadRef.current === currentPath) return;
    lastPreloadRef.current = currentPath;

    // Get likely next pages
    const likelyNextPages = navigationPatterns[currentPath] || [];
    
    // Preload after a short delay (let current page render first)
    const timer = setTimeout(() => {
      preloadPages(likelyNextPages);
    }, 1000);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Preload on link hover
  const preloadOnHover = useCallback((path) => {
    preloadPage(path);
  }, []);

  // Preload on link focus (for keyboard navigation)
  const preloadOnFocus = useCallback((path) => {
    preloadPage(path);
  }, []);

  return {
    preloadOnHover,
    preloadOnFocus,
    preloadPage,
    preloadPages,
  };
};

/**
 * Link wrapper component that preloads on hover
 */
export const PreloadLink = ({ to, children, className, ...props }) => {
  const { preloadOnHover, preloadOnFocus } = useIntelligentPreload();

  return (
    <a
      href={to}
      className={className}
      onMouseEnter={() => preloadOnHover(to)}
      onFocus={() => preloadOnFocus(to)}
      {...props}
    >
      {children}
    </a>
  );
};

/**
 * Preload critical pages on app start
 */
export const preloadCriticalPages = () => {
  // Wait for initial render, then preload dashboard
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      preloadPage('/dashboard');
    }, { timeout: 3000 });
  } else {
    setTimeout(() => preloadPage('/dashboard'), 2000);
  }
};

export default useIntelligentPreload;
